import { CITED_TEXT_FRAMING } from 'features/knowledgeDocumentViewer/components/viewer/shared/regionFraming'
import type { FrameTarget } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import type { FragmentHighlightState } from 'features/knowledgeDocumentViewer/hooks/useFragmentHighlight'
import type { IntraDocSearchHit } from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import type { FragmentHighlight } from 'features/knowledgeDocumentViewer/types'
import { unionNormalizedBBoxes } from 'features/knowledgeDocumentViewer/utils/bboxHelpers'
import { useMemo } from 'react'

interface HitFrameInputs {
  regionHit: IntraDocSearchHit | null
  fragmentHighlight: FragmentHighlight | undefined
  fragment: Pick<FragmentHighlightState, 'isActive' | 'hit'>
  intraSearchIsOpen: boolean
}

interface HitFrame {
  citedFragmentHit: IntraDocSearchHit | null
  frameTarget: FrameTarget | undefined
}

export function resolveHitFrame({
  regionHit,
  fragmentHighlight,
  fragment,
  intraSearchIsOpen,
}: HitFrameInputs): HitFrame {
  const citedFragmentHit =
    fragmentHighlight?.quotes && fragment.isActive ? fragment.hit : null
  const framedHit = regionHit ?? citedFragmentHit
  if (!framedHit || intraSearchIsOpen) {
    return { citedFragmentHit, frameTarget: undefined }
  }
  return {
    citedFragmentHit,
    frameTarget: {
      bbox: unionNormalizedBBoxes(framedHit.bounding_boxes),
      pageNumber: framedHit.page_number,
      framing: regionHit ? undefined : CITED_TEXT_FRAMING,
    },
  }
}

export function useCitedTextFrameTarget({
  regionHit,
  fragmentHighlight,
  fragment,
  intraSearchIsOpen,
}: HitFrameInputs): HitFrame {
  const { isActive, hit } = fragment
  return useMemo(
    () =>
      resolveHitFrame({
        regionHit,
        fragmentHighlight,
        fragment: { isActive, hit },
        intraSearchIsOpen,
      }),
    [regionHit, fragmentHighlight, isActive, hit, intraSearchIsOpen]
  )
}
